import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';

import LoadingPage from '@/components/common/LoadingPage';
import SubPageHeader from '@/components/common/SubPageHeader';
import { ToggleGroup, ToggleGroupItem } from '@/components/shadcn/toggle-group';
import { dateOrUndefined } from '@/lib/utils/date';
import formatCurrency from '@/lib/utils/formatCurrency';

import useSelectedParty from '../analysis/hooks/useSelectedParty';

import InvoiceItem from './components/InvoicesSection/InvoiceItem';
import useInvoices from './queries/hooks/useInvoices';
import type { Invoice } from './types';

const getGroupedInvoices = (invoices: Maybe<Invoice[]>) =>
  invoices?.reduce(
    (groups, invoice) => {
      const date = dateOrUndefined(invoice.invoice_date);

      if (!date) return groups;

      const year = date.getFullYear();

      return {
        ...groups,
        [year]: [...(groups[year] ?? []), invoice],
      };
    },
    {} as Record<string, Invoice[]>,
  );

export default function InvoicesOverview() {
  const { t } = useTranslation();

  const [searchParams, setSearchParams] = useSearchParams();

  const outstandingOnly = searchParams.get('outstanding') === 'true';

  const handleSetOutstandingOnly = (value?: string) =>
    setSearchParams(
      {
        outstanding: (value ?? !outstandingOnly).toString(),
      },
      { replace: true },
    );

  const party = useSelectedParty();

  const { data: outstandingInvoices } = useInvoices(
    party?.id ?? '',
    { status: ['OPEN', 'PARTIALLY_PAID'], orderBy: 'due_date', sort: 'desc' },
    { enabled: Boolean(party?.id) },
  );

  const totalOutstandingAmount = outstandingInvoices?.reduce(
    (total, invoice) => (total += invoice?.outstanding_amount ?? 0),
    0,
  );

  const { data: invoices, isLoading } = useInvoices(
    party?.id ?? '',
    {
      status: outstandingOnly ? ['OPEN', 'PARTIALLY_PAID'] : [],
      orderBy: 'due_date',
      sort: 'desc',
    },
    { enabled: Boolean(party?.id) },
  );

  const groupedInvoices = getGroupedInvoices(invoices);

  return (
    <div className="flex h-screen flex-col overflow-hidden">
      <SubPageHeader>{t('INVOICES_TITLE')}</SubPageHeader>

      <main className="flex flex-1 flex-col gap-2 overflow-y-auto pb-4">
        <div className="mt-2 flex flex-col gap-1 p-2">
          <ToggleGroup
            type="single"
            value={searchParams.get('outstanding') ?? 'true'}
            onValueChange={handleSetOutstandingOnly}
            className="border"
          >
            <ToggleGroupItem value="true" className="cursor-pointer">
              {t('INVOICES_FILTER_OVERDUE')}
            </ToggleGroupItem>

            <ToggleGroupItem value="false" className="cursor-pointer">
              {t('INVOICES_FILTER_ALL')}
            </ToggleGroupItem>
          </ToggleGroup>

          {outstandingInvoices && (
            <div className="">
              {t('INVOICES_OVERDUE')}: {outstandingInvoices?.length} (
              {formatCurrency(totalOutstandingAmount)})
            </div>
          )}
        </div>

        {isLoading && <LoadingPage />}

        {!isLoading && invoices && (
          <div className="flex flex-col gap-4 px-2">
            {groupedInvoices &&
              Object.entries(groupedInvoices)
                .sort(([yearA], [yearB]) => parseInt(yearB) - parseInt(yearA))
                .map(([year, invoices]) => (
                  <div key={year} className="flex flex-col gap-2">
                    <div className="flex items-center justify-between">
                      <span className="ml-1 uppercase">{year}</span>

                      <span className="flex size-6 items-center justify-center rounded-full bg-neutral-200 text-sm text-neutral-700 dark:bg-neutral-800 dark:text-neutral-400">
                        {invoices.length}
                      </span>
                    </div>

                    <div className="flex flex-col gap-1">
                      {invoices.map((invoice) => (
                        <InvoiceItem key={invoice.id} invoice={invoice} />
                      ))}
                    </div>
                  </div>
                ))}
          </div>
        )}
      </main>
    </div>
  );
}
